'use client'

// src/app/global-error.tsx
// Top-level error boundary for the entire app. Renders when an unhandled
// error escapes every nested error boundary (including the root layout),
// so it must provide its own <html>/<body> shell.

import { useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <html lang="en">
      <body className="bg-background text-foreground antialiased">
        <div className="flex min-h-dvh items-center justify-center px-6 py-12">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
            <h1 className="mb-2 text-xl font-semibold text-foreground">Something went wrong</h1>
            <p className="mb-6 text-sm text-muted-foreground">
              An unexpected error occurred. Our team has been notified and we&apos;re looking into
              it.
            </p>
            {error.digest ? (
              <p className="mb-6 font-mono text-xs text-muted-foreground">
                Error ID: {error.digest}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => reset()}
              className="inline-flex items-center justify-center rounded-md border border-border bg-muted px-4 py-2 text-sm font-medium text-foreground transition hover:bg-foreground/10 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              Try again
            </button>
          </div>
        </div>
      </body>
    </html>
  )
}
